import { View } from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKButton,
  CKCompanyLogo,
  CKContainerProps,
  CKLayoutHorizontal,
} from 'clearkit';
import { Link } from 'react-router-dom';

import { routes } from '~/components/app/appMainRoutes';
import { ListItemCheckbox } from '~/components/ListItem/ListItemCheckbox';
import { ListItemGrid } from '~/components/ListItem/ListItemGrid';
import { AVATAR_IMAGE_SIZE } from '~/components/prospector/ProspectList/constants';
import { LoadingListItem } from '~/components/prospector/ProspectList/LoadingList';
import ProspectorButton from '~/components/tools/ProspectorButton';
import { ButtonContainer } from '~/components/unified/ButtonContainer';
import { EMPTY_LABEL } from '~/lib/constants';
import { trackEvent } from '~/lib/tracking';

import { TargetMarketsCompany } from '../types';
import { CompanyLinks } from './CompanyLinks';

type CompanyItemProps = {
  company: TargetMarketsCompany;
  loading: boolean;
  isSelected: boolean;
  onSelect: (domain: string, isChecked: boolean) => void;
  number: number;
};

const Field = ({ className, children }: CKContainerProps) => {
  return (
    <p
      className={classnames(
        'text-xs text-gray-600 truncate whitespace-nowrap',
        className,
      )}
    >
      {children}
    </p>
  );
};

export const CompanyItem = ({
  company,
  loading,
  isSelected,
  onSelect,
  number,
}: CompanyItemProps) => {
  const {
    country,
    domain,
    encryptedDomain,
    employeesRange,
    industry,
    name,
  } = company;

  const handleSelect = () => {
    onSelect(domain, !isSelected);
  };

  const handleTrackingView = () => {
    trackEvent('target_market_company_clicked', { domain });
  };

  return loading ? (
    <LoadingListItem />
  ) : (
    <ListItemCheckbox
      id={domain}
      isSelected={isSelected}
      onSelect={handleSelect}
    >
      <ListItemGrid gridTemplateColumns="4fr 3fr">
        <CKLayoutHorizontal
          alignItems="start"
          className="min-h-0 @container basis-full shrink grow"
          justifyContent="start"
          wrap="nowrap"
        >
          <div className="text-right text-md mt-2.5 w-[3ch]">#{number}</div>
          <CKLayoutHorizontal
            alignItems="start"
            className="shrink grow"
            justifyContent="start"
            wrap="nowrap"
          >
            <CKCompanyLogo
              className="shrink-0"
              domain={domain ?? ''}
              size={AVATAR_IMAGE_SIZE}
            />
            <div className="space-y-2">
              <h3 className="text-sm font-medium truncate whitespace-nowrap">
                {name}
              </h3>
              <CompanyLinks company={company} />
            </div>
          </CKLayoutHorizontal>
        </CKLayoutHorizontal>
        <ListItemGrid.Right>
          <Field className="font-medium !text-gray-800">
            {country || 'Unknown Country'}
          </Field>
          <Field>{industry || EMPTY_LABEL}</Field>
          {employeesRange ? <Field>{employeesRange} employees</Field> : null}
        </ListItemGrid.Right>
      </ListItemGrid>
      <ButtonContainer className="shrink-0 basis-auto" gap=".5rem">
        <ProspectorButton
          domain={domain}
          loading={loading}
          name={name}
          type="target_markets"
        />
        <CKButton
          aria-label="View company"
          as={Link}
          className="!px-2"
          onClick={handleTrackingView}
          size="small"
          to={`${routes.targetMarkets}/${encryptedDomain}`}
        >
          <View />
        </CKButton>
      </ButtonContainer>
    </ListItemCheckbox>
  );
};
